import React, { useEffect, useState } from "react";

import { IOrganizationForTree, IOrganizationsTreeProps, ITreeItem } from "../../interfaces/tree";
import { EtyEmployer } from "../../interfaces/entities";

import { setItemsRecursive, resetSelected, findItemRecursive, removeTemporaryItems } from "../../helpers/tree";

import { Icons } from "../../data/Assets";

import { TreeItem } from "../reusables/TreeItem";
import { SVGIcon } from "../wrappers/SVGIcon";

const LAST_LEVEL = 1;
const OrganizationsTree = (props: IOrganizationsTreeProps) => {
    const { organizations, selected, setSelected, expanded, setExpanded } = props;

    const [items, setItems] = useState<ITreeItem[]>([]);

    useEffect(() => {
        if (organizations.length > 0) {
            setItems(setItemsRecursive({
                type: 'organization',
                items: organizations,
                onClickItem,
                selected,
                expanded,
                parentMethod: null,
                lastLevel: LAST_LEVEL,
            }));
        } else {
            setItems([]);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizations]);

    const onClickItem = (item: ITreeItem) => {
        let cExpanded = false;
        setExpanded(prevData => {
            const newData = [...prevData];

            const eItemIdx = newData.findIndex(itm => itm.id === item.id && itm.level === item.level);
            if (eItemIdx === -1) {
                newData.push({
                    id: item.id,
                    level: item.level,
                });
                cExpanded = true;
            } else {
                const itm = findItemRecursive(items, item.id, item.level);
                if (itm?.selected) {
                    newData.splice(eItemIdx, 1);
                    cExpanded = false;
                } else {
                    cExpanded = true;
                };
            };

            return newData;
        });

        setSelected({
            id: item.id,
            level: item.level,
            data: item.data,
        });

        setItems(prevState => {
            const newItems = resetSelected([...prevState]);

            const itm = findItemRecursive(newItems, item.id, item.level);
            if (itm) {
                itm.selected = true;
                itm.expanded = cExpanded;
            };

            return newItems;
        });
    };

    const getDisabled = (type: 'organization' | 'employer') => {
        if (selected?.id === 0) return true;

        if (type === 'organization') {
            return false;
        } else if (type === 'employer') {
            if (selected) {
                if (selected.level === 0) {
                    return false;
                } else if (selected.level === 1) {
                    return selected.id === 0;
                } else {
                    return true;
                }
            } else {
                return true;
            };
        }

        return true;
    };

    useEffect(() => {
        if (!selected || selected.id > 0) {
            setItems(prevState => {
                return removeTemporaryItems([...prevState]);
            });

            setExpanded(prevData => {
                return prevData.filter(item => item.id > 0);
            });
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    const onAddNewOrganization = () => {
        if (getDisabled('organization')) return;

        setItems(prevState => {
            return removeTemporaryItems([...prevState]);
        });

        const data: IOrganizationForTree = {
            id: 0,
            name: '',
            status: 'setup',
            status_update: '',
            employers: [],
        };

        setItems(prevState => {
            const newItems = resetSelected([...prevState]);
            newItems.unshift({
                type: 'organization',
                id: data.id,
                parentId: null,
                level: 0,
                name: "New Organization",
                method: null,
                parentMethod: null,
                children: [],
                data: data,
                selected: true,
                expanded: false,
                onClickItem: () => { },
                lastLevel: LAST_LEVEL,
            });
            return newItems;
        });
        setSelected({
            id: data.id,
            level: 0,
            data: data,
        });
    };

    const onAddNewEmployer = () => {
        if (getDisabled('employer') || !selected) return;

        let organization_id = 0;
        if (selected.level === 0) {
            const selectedOrganization = findItemRecursive(items, selected.id, 0);
            organization_id = selectedOrganization?.data?.id ?? 0;
        } else if (selected.level === 1) {
            const selectedEmployer = findItemRecursive(items, selected.id, 1);
            organization_id = (selectedEmployer?.data as EtyEmployer | null)?.organization_id ?? 0;
        } else {
            return;
        }

        if (!organization_id) return;

        const data: EtyEmployer = {
            id: 0,
            organization_id,
            type: 'corporation',
            operating_name: '',
            legal_name: '',
            naics: '',
            cra_number: '',
            tech_company: true,
            non_canadian_emp: 0,
            lmia_in_past: null,
        };

        const oOrganization = findItemRecursive([...items], organization_id, 0);
        if (oOrganization?.expanded === false) {
            onClickItem(oOrganization);
        };

        setItems(prevState => {
            const newItems = resetSelected([...prevState]);

            const organization = findItemRecursive(newItems, organization_id, 0);
            if (organization) {
                organization.children.unshift({
                    type: 'employer',
                    id: data.id,
                    parentId: organization.id,
                    level: 1,
                    name: "New Employer",
                    method: null,
                    parentMethod: null,
                    children: [],
                    data: data,
                    selected: true,
                    expanded: true,
                    onClickItem: () => { },
                    lastLevel: LAST_LEVEL,
                });
            };
            return newItems;
        });
        setSelected({
            id: data.id,
            level: 1,
            data: data,
        });
    };

    return (
        <div className="immployer_admin__tree_component">
            <div className="immployer_admin__tree_top">
                <button
                    className={`immployer_admin__tree__action_btn ${getDisabled('organization') ? 'immployer_admin__tree__action_btn__disabled' : ''}`}
                    onClick={onAddNewOrganization}
                >
                    <SVGIcon src={Icons.Plus} />
                    <span>Organization</span>
                </button>
                <button
                    className={`immployer_admin__tree__action_btn ${getDisabled('employer') ? 'immployer_admin__tree__action_btn__disabled' : ''}`}
                    onClick={onAddNewEmployer}
                >
                    <SVGIcon src={Icons.Plus} />
                    <span>Employer</span>
                </button>
            </div>
            <div className="immployer_admin__tree_body">
                {items.length > 0
                    ? items.map(item =>
                        <TreeItem
                            type='organization'
                            key={item.id}
                            id={item.id}
                            parentId={item.parentId}
                            level={item.level}
                            name={item.name}
                            method={null}
                            parentMethod={null}
                            onClickItem={onClickItem}
                            children={item.children}
                            data={item.data}
                            selected={item.selected}
                            expanded={item.expanded}
                            lastLevel={LAST_LEVEL}
                        />
                    )
                    : <div className="immployer_admin__tree__empty">No organizations yet.</div>
                }
            </div>
        </div>
    );
};

export default OrganizationsTree;